import { useId, useState } from 'react';

import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import {
  createTemplateFromEntry,
  type AmountMode,
  type RecurringFrequency,
} from '../data/recurringRepository.js';
import { AMOUNT_MODE_LABELS, FREQUENCY_LABELS } from '../model/recurringLabels.js';

/**
 * "Lưu thành định kỳ" — the entry on screen becomes next month's forecast (N9, step 1.5).
 *
 * It asks for the four things the entry cannot know: what to call it, how often, when it is
 * next due, and whether the amount is the same every time. Everything else — accounts,
 * categories, and every line of a split — is copied by the RPC from the entry itself.
 */
export function SaveAsTemplate({
  entryId,
  suggestedName,
  defaultFirstDue,
  onSaved,
  onCancel,
}: {
  entryId: string;
  suggestedName: string;
  /** YYYY-MM-DD — normally one period after the entry, but the user has the last word. */
  defaultFirstDue: string;
  onSaved: (name: string) => void;
  onCancel: () => void;
}) {
  const nameId = useId();
  const frequencyId = useId();
  const dueId = useId();

  const [name, setName] = useState(suggestedName);
  const [frequency, setFrequency] = useState<RecurringFrequency>('monthly');
  const [amountMode, setAmountMode] = useState<AmountMode>('fixed');
  const [firstDue, setFirstDue] = useState(defaultFirstDue);
  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  const canSave = name.trim() !== '' && firstDue !== '';

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setMessage(null);
    try {
      await createTemplateFromEntry({
        entryId,
        name: name.trim(),
        frequency,
        // Same fact as the due date, so it is never asked for twice.
        dayOfMonth: frequency === 'weekly' ? null : Number(firstDue.slice(8, 10)),
        amountMode,
        firstDue,
      });
      onSaved(name.trim());
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={forms.form}>
      <div className={forms.field}>
        <label htmlFor={nameId}>Tên khoản định kỳ</label>
        <input
          id={nameId}
          className={forms.input}
          type="text"
          value={name}
          autoComplete="off"
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      <div className={forms.field}>
        <label htmlFor={frequencyId}>Tần suất</label>
        <select
          id={frequencyId}
          className={forms.input}
          value={frequency}
          onChange={(event) => setFrequency(event.target.value as RecurringFrequency)}
        >
          {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className={forms.field}>
        <label htmlFor={dueId}>Kỳ kế tiếp</label>
        <input
          id={dueId}
          className={forms.input}
          type="date"
          value={firstDue}
          onChange={(event) => setFirstDue(event.target.value)}
        />
      </div>

      <fieldset className={forms.field}>
        <legend>Số tiền</legend>
        {(['fixed', 'estimated'] as const).map((mode) => (
          <label key={mode} className={forms.hint}>
            <input
              type="radio"
              name="save-template-amount-mode"
              value={mode}
              checked={amountMode === mode}
              onChange={() => setAmountMode(mode)}
            />{' '}
            {AMOUNT_MODE_LABELS[mode]}
          </label>
        ))}
      </fieldset>

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <div className={forms.actions}>
        <button type="button" className={forms.secondary} onClick={onCancel}>
          Hủy
        </button>
        <button
          type="button"
          className={forms.primary}
          disabled={!canSave || isSaving}
          onClick={() => void handleSave()}
        >
          {isSaving ? 'Đang lưu…' : 'Lưu thành định kỳ'}
        </button>
      </div>
    </div>
  );
}
